import { Box } from "@mui/material";
import { useMe } from "../../../auth/MeContext";
import { PERM } from "../../../auth/permissions";
import { Button, Form, Input, message, Modal, Space, Switch, Table, Tag } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../axios/axios-instance";
import { pathAPI } from "../../../utils/pathApi";

interface LanguageOption {
  id: number;
  name: string;
  language_code: string;
}

interface Province {
  id: number;
  name: string;
  active?: boolean;
  translations?: {
    language_id: number;
    name: string;
  }[];
}

interface LocationModalProps {
  open: boolean;
  onClose: () => void;
  countryId?: number;
  countryName?: string;
  languageCode: string;
}

const LocalModal: React.FC<LocationModalProps> = ({ open, onClose, countryId, countryName, languageCode }) => {
  const { t } = useTranslation();
  const { can } = useMe();
  const canCreate = can(PERM.COUNTRY_CREATE);
  const canUpdate = can(PERM.COUNTRY_UPDATE);
  const canDelete = can(PERM.COUNTRY_DELETE);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [loadingProvinces, setLoadingProvinces] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingActive, setEditingActive] = useState(true);
  const [languages, setLanguages] = useState<LanguageOption[]>([]);
  const [editingTranslations, setEditingTranslations] = useState<{ [key: number]: string }>({});

  // Fetch languages
  const fetchLanguages = async () => {
    try {
      const response = await axiosInstance.get(pathAPI.languages);
      if (response.data?.data) {
        setLanguages(response.data.data);
      }
    } catch (error) {
      message.error('Failed to load languages');
    }
  };

  useEffect(() => {
    if (open) {
      fetchLanguages();
      if (countryId) {
        fetchProvinces();
      }
    }
  }, [open, countryId]);

  const fetchProvinces = async () => {
    if (!countryId) return;

    try {
      setLoadingProvinces(true);
      const response = await axiosInstance.get(`${pathAPI.cities.replace(":slug", countryId.toString())}`);
      setProvinces(response.data.data);
    } catch (error) {
      message.error('Failed to fetch provinces');
    } finally {
      setLoadingProvinces(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setEditingId(null);
    setEditingName('');
    setEditingActive(true);
    setEditingTranslations({});
    onClose();
  };

  const handleSubmit = async (values: any) => {
    if (!countryId) {
      message.error('Country ID is required');
      return;
    }

    setLoading(true);
      await axiosInstance.post(`${pathAPI.countries}/${countryId}/cities`, {
        province_name: values.provinceName,
        active: values.active ?? true,
      }).then(() => {
        message.success('Province added successfully');
        form.resetFields();
      }).catch((err) => {
        message.error('Failed to add province');
      }).finally(() => {
        setLoading(false);
      });
  };

  const handleEdit = (record: Province) => {
    setEditingId(record.id);
    setEditingName(record.name);
    setEditingActive(record.active ?? true);

    // Initialize translations
    const translations: { [key: number]: string } = {};
    record.translations?.forEach(trans => {
      translations[trans.language_id] = trans.name;
    });
    setEditingTranslations(translations);
  };

  const handleSave = async (id: number) => {
    try {
      setLoading(true);
      const translations = Object.entries(editingTranslations).map(([langId, name]) => ({
        language_id: parseInt(langId),
        name
      }));

      await axiosInstance.put(`${pathAPI.countries}/${countryId}/cities/${id}`, {
        name: editingName,
        active: editingActive,
        translations
      });

      message.success('Province updated successfully');
      setEditingId(null);
      setEditingName('');
      setEditingActive(true);
      setEditingTranslations({});
      fetchProvinces();
    } catch (error) {
      message.error('Failed to update province');
    } finally {
      setLoading(false);
    }
  };

  const handleTranslationChange = (langId: number, value: string) => {
    setEditingTranslations(prev => ({
      ...prev,
      [langId]: value
    }));
  };

  const handleDeleteProvince = async (id: number) => {
    try {
      setLoading(true);
      await axiosInstance.delete(`${pathAPI.countries}/${countryId}/cities/${id}`);
      message.success('Province deleted successfully');
      fetchProvinces();
    } catch (error) {
      message.error('Failed to delete province');
    } finally {
      setLoading(false);
    }
  };

  const handleDiscard = () => {
    setEditingId(null);
    setEditingName('');
    setEditingActive(true);
    setEditingTranslations({});
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center' as const,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Province) => {
        if (editingId === record.id) {
          return (
            <Input
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              style={{ width: '100%' }}
            />
          );
        }
        return text;
      },
    },
    {
      title: 'Translations',
      key: 'translations',
      render: (_: unknown, record: Province) => {
        if (editingId === record.id) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {languages.map(lang => (
                <Input
                  key={lang.id}
                  placeholder={`${lang.name} translation`}
                  value={editingTranslations[lang.id] || ''}
                  onChange={(e) => handleTranslationChange(lang.id, e.target.value)}
                />
              ))}
            </div>
          );
        }
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {record.translations?.map(trans => {
              const lang = languages.find(l => l.id === trans.language_id);
              return lang ? (
                <div key={lang.id} style={{ fontSize: '0.9em', color: 'var(--app-text-muted)' }}>
                  {lang.name}: {trans.name}
                </div>
              ) : null;
            })}
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      width: 100,
      align: 'center' as const,
      render: (active: boolean, record: Province) => {
        if (editingId === record.id) {
          return (
            <Switch
              checked={editingActive}
              onChange={setEditingActive}
            />
          );
        }
        return (
          <Tag color={active ? 'success' : 'error'}>
            {active ? 'Active' : 'Inactive'}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_: any, record: Province) => (
        <>
          {editingId === record.id ? (
            <Space>
              <Button type="primary" onClick={() => handleSave(record.id)} loading={loading}>
                Save
              </Button>
              <Button onClick={handleDiscard}>Cancel</Button>
            </Space>
          ) : (
            <Space>
              <Button type="primary" disabled={!canUpdate} onClick={() => handleEdit(record)}>
                Edit
              </Button>
              <Button danger disabled={!canDelete} onClick={() => handleDeleteProvince(record.id)}>
                Delete
              </Button>
            </Space>
          )}
        </>
      ),
    },
  ];

  return (
    <Modal
      title={`Province Management - ${countryName}`}
      open={open}
      onCancel={handleClose}
      width={800}
      footer={null}
    >
      <Box sx={{ mt: 2 }}>
        {/* Add New Province Section */}
        {canCreate && (
        <Box sx={{
          p: 2,
          mb: 3,
          border: '1px solid var(--app-border-light)',
          borderRadius: '8px',
          backgroundColor: 'var(--app-bg)'
        }}>
          <h3 style={{ marginBottom: 16 }}>Add New Province</h3>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ active: true }}
          >
            <Form.Item
              name="provinceName"
              label="Province Name"
              rules={[{ required: true, message: 'Please input the province name!' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="active"
              label="Status"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                Add Province
              </Button>
              <Button onClick={handleClose} style={{ marginLeft: 8 }}>
                Cancel
              </Button>
            </Form.Item>
          </Form>
        </Box>
        )}

        {/* Provinces List Section */}
        <Box>
          <h3 style={{ marginBottom: 16 }}>Provinces List</h3>
          <Table
            columns={columns}
            dataSource={provinces}
            rowKey="id"
            loading={loadingProvinces}
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              showTotal: (total) => `Total ${total} items`
            }}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default LocalModal;
